// This file is a temporary solution.
// TODO: we should move this logic to 1) getters OR 2) A service that receives store

import get from 'lodash/get';

import DataChipBridgeHelper from './data-chip-bridge-helper';
import { INSERT_DATA_DELIMITER_PREFIX as PREFIX, INSERT_DATA_DELIMITER_SUFFIX as SUFFIX } from './data-chip.const';
import AppService from '../../../../utils/AppService';
import WidgetService from '../../../../utils/WidgetService';
import * as WorkflowNodeService from '../../../../utils/workflow-node-service';
import FormatConversionService from '../format-conversion-service';
import HtmlDocumentService from '@/components/common/RichTextBox/HtmlAttributes/html-document-service.js';
import store from '@/store';
import { getService } from '@/store/modules/apps/getters';
import { getPreviousStepDataForNodeByUUID, getGlobalWorkflowData } from '@/store/modules/nodes/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import {
	selectedWorkflowId,
	processWorkflowIds,
	nodesData,
	replaceInsertDataStringDictionary,
} from '@/store/modules/selectedWorkflow/getters';
import {
	configurationsMappingBlocksUUIDs,
	getConfigurationAndInterfaceIdByFieldUUID,
} from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getWidget } from '@/store/modules/widgets/getters';
import { getWorkflowDraftNodesDataByUUID, nodesDataSorted } from '@/store/modules/workflows/getters';
import ParamsService from '@/utils/ParamsService';
import { isNullOrUndefined } from '@/utils/helpers';
import { GLOBAL_RUN_DATA, PLY_ICON_SERVICE_SLUG } from '@/utils/workflow-node.const';

/* Private */
/**
 * Get node object from node UUID.
 * We search in selected workflow first, then try in process workflows, should they exist.
 */
function findStepInfo(nodeUUID) {
	const getters = store.getters;
	let node = null;
	let stepNumber = null;
	const processWfIds = getters[processWorkflowIds.fullPath] || [];
	for (const wfId of [getters[selectedWorkflowId.fullPath], ...processWfIds]) {
		stepNumber = getters[nodesDataSorted.fullPath](wfId)?.findIndex((n) => n.uuid === nodeUUID) + 1;
		node = getters[getWorkflowDraftNodesDataByUUID.fullPath]({ workflowId: wfId })[nodeUUID];
		if (stepNumber && node) {
			break;
		}
	}
	return { node, stepNumber };
}

/* Public */

function getChipData(code, nodeId, options, isRichTextEnabled) {
	const wrappedCode = `${PREFIX}${code}${SUFFIX}`; // so it works with regex
	const isGlobals = code.includes(GLOBAL_RUN_DATA);
	const nodeUUID = ParamsService.extractNodeUUIDFromValue(wrappedCode);
	const getters = store.getters;

	const chipNodeUUID = getters[editNode.fullPath]?.uuid;
	const chipNodeStepInfo = findStepInfo(chipNodeUUID);

	const { node, stepNumber } = findStepInfo(nodeUUID);

	if ((!node || chipNodeStepInfo.stepNumber < stepNumber) && !isGlobals) {
		return null;
	}

	let key = ParamsService.extractPathFromValue(
		wrappedCode,
		getters[replaceInsertDataStringDictionary.fullPath],
		getters[configurationsMappingBlocksUUIDs.fullPath]
	);
	let appData = {};
	let paramValue = null;
	let previousStepData = {};

	if (isGlobals) {
		appData = { service_slug: PLY_ICON_SERVICE_SLUG };
		previousStepData = getters[getGlobalWorkflowData.fullPath](getters[selectedWorkflowId.fullPath]);
	} else {
		appData = getters[getService.fullPath](node.service);
		previousStepData = getters[getPreviousStepDataForNodeByUUID.fullPath](nodeUUID);
	}

	const param = get(previousStepData, key);

	const hasAdditionValue = !isNullOrUndefined(param);
	if (hasAdditionValue) {
		if (Array.isArray(param)) {
			const length = param.length;
			paramValue = `${length} ${length > 1 ? 'items' : 'item'}`;
		} else {
			paramValue = `${param}`;
		}
	}

	const isWidget = AppService.isSlugOfWidget(appData?.service_slug);
	if (isWidget) {
		const widgetId = WorkflowNodeService.getNodeWidgetId(node);
		const widget = getters[getWidget.fullPath](widgetId);
		if (widget) {
			key = WidgetService.replaceKeyWithIdentifier(widget, key);
			if (!key) {
				return null;
			}
		}
	}

	// TODO: are we assuming that only markdown returns type in previous step data?
	if (previousStepData?.type) {
		paramValue = FormatConversionService.convertMarkdownToHtmlForTiptapPresentation(paramValue);
	}

	if (!paramValue) {
		throw new Error('missing chip content');
	}

	paramValue = HtmlDocumentService.inlineCssToHtmlElements(paramValue);

	const paramResult = DataChipBridgeHelper.applyConversions(paramValue, nodeId, options, isRichTextEnabled);

	const isParamFromConfiguration = ParamsService.isPathOfConfiguration(key);
	if (isParamFromConfiguration) {
		const [, configurationFieldUUID, ...keySuffixParts] = key?.split('.');

		const configurationInterfaceUUID = getters[getConfigurationAndInterfaceIdByFieldUUID.fullPath](configurationFieldUUID)?.interfaceUUID;
		const widget = getters[getWidget.fullPath](configurationInterfaceUUID);
		if (widget) {
			key = WidgetService.replaceKeyWithIdentifier(widget, configurationFieldUUID, {
				keySuffixParts,
				getService: getters[getService.fullPath],
			});
			if (!key) {
				return null;
			}
		}
	}
	return {
		step: stepNumber,
		app: appData,
		fieldKey: key,
		param: paramResult,
	};
}

function getChipDataProcessedCode(code) {
	const getters = store.getters;
	return get(getters[replaceInsertDataStringDictionary.fullPath], code, code);
}

function getChipDataFromMultiNode(code, nodeId, options) {
	const getters = store.getters;
	const processedCode = getChipDataProcessedCode(code);
	const appData = getters[getService.fullPath](getters[nodesData.fullPath][nodeId].service);
	let codeItem = options.find((option) => option.key === processedCode);
	let paramValue = codeItem ? codeItem.example : '';
	if (!codeItem) {
		codeItem = options.find((option) => get(option, processedCode));
		if (codeItem) {
			paramValue = get(codeItem, processedCode);
		}
	}
	paramValue = paramValue || '***';
	return { app: appData, param: `${paramValue}` };
}

const DataChipBridge = {
	getChipData,
	getChipDataFromMultiNode,
	getChipDataProcessedCode,
};

export default DataChipBridge;
